import { ChangeEvent, FC, useRef } from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import { Box, TextField } from "@mui/material";
import { InputTextProps } from "./InputText";
import { useTranslation } from "react-i18next";

type OtpInputFieldProps = {
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  error: boolean;
  errorMessage?: string;
  inputRef: (ref: any) => void;
  sx?: any;
  rest?: any;
};

const OtpInputField: FC<OtpInputFieldProps> = ({
  value,
  onChange,
  onKeyDown,
  error,
  errorMessage,
  inputRef,
  sx,
  rest,
}) => {
  const { t } = useTranslation();

  return (
    <TextField
      ref={inputRef}
      variant="outlined"
      type="text"
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
      error={error || false}
      color={error ? "error" : "primary"}
      helperText={t(errorMessage ?? "")}
      fullWidth
      {...rest}
      sx={{
        width: 48,
        height: 56,
        textAlign: "center",
        fontSize: 20,
        borderRadius: 8,
        "& .MuiInputBase-root": {
          backgroundColor: "custom.background.secondary",
        },
        "& .MuiInputBase-root.Mui-focused": {
          backgroundColor: "custom.background.primary",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderWidth: "1px",
          borderColor: error
            ? "custom.foreground.attention"
            : "custom.border.default",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
          {
            borderWidth: "1px",
            borderColor: error
              ? "custom.foreground.attention"
              : "custom.border.strong",
          },
        ...sx,
      }}
    />
  );
};

type OtpFieldsProps = {
  length: number;
  fieldValue: string;
  errorMessage?: string;
  isError?: boolean;
  handleChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => void;
  handleKeyDown: (e: React.KeyboardEvent, index: number) => void;
  inputRefs: React.MutableRefObject<any[]>;
  sx?: any;
  rest?: any;
};

const OtpFields: FC<OtpFieldsProps> = ({
  length,
  fieldValue,
  errorMessage,
  isError,
  handleChange,
  handleKeyDown,
  inputRefs,
  sx,
  rest,
}) => (
  <Box display="flex" justifyContent="center" gap={1}>
    {Array.from({ length }, (_, index) => (
      <OtpInputField
        key={`otp-field-${index}`}
        value={fieldValue[index]}
        onChange={(e) => handleChange(e, index)}
        onKeyDown={(e) => handleKeyDown(e, index)}
        error={!!errorMessage || !!isError}
        errorMessage={errorMessage}
        inputRef={(ref) => (inputRefs.current[index] = ref!)}
        sx={sx}
        rest={rest}
      />
    ))}
  </Box>
);

export type InputOtpProps = {
  length?: number;
  onOtpComplete?: (otp: string) => void;
  control: Control<any>;
  name: string;
  rules?: Omit<
    RegisterOptions<any, string>,
    "disabled" | "setValueAs" | "valueAsNumber" | "valueAsDate" | "type"
  >;
  isError?: boolean;
  autoFocus?: boolean;
} & Omit<InputTextProps, "label" | "type" | "isLabelDefaultBehaviour">;

const InputOtp: FC<InputOtpProps> = ({
  length = 4,
  onOtpComplete,
  control,
  name,
  rules,
  style,
  isError,
  autoFocus,
  sx,
  ...rest
}) => {
  const inputRefs = useRef<Array<any>>([]);

  return (
    <Controller
      control={control}
      rules={rules}
      name={name}
      render={({ field, fieldState }) => {
        const errorMessage = fieldState?.error?.message;
        const fieldValue = field?.value?.toString();

        const handleChange = (
          e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          index: number
        ) => {
          const val = e.target.value;
          if (val === "" || /^[0-9\b]+$/.test(val)) {
            const lastChar = val.slice(-1);

            const newValue = fieldValue
              ? fieldValue.slice(0, index) +
                lastChar +
                fieldValue.slice(index + 1)
              : lastChar.padEnd(length, " ");

            field.onChange(newValue.trim());

            if (val && index < length - 1) {
              setTimeout(() => {
                inputRefs.current?.[index + 1]?.querySelector("input")?.focus();
              }, 0);
            }

            if (fieldValue.length === length) {
              onOtpComplete?.(fieldValue);
            }
          }
        };

        const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
          if (e.key === "Backspace") {
            field.onChange(fieldValue.slice(0, -1));
            if (index > 0 && index <= length - 1 && !fieldValue[index]) {
              inputRefs.current[index - 1]?.querySelector("input")?.focus();
            }
          }
        };

        return (
          <OtpFields
            length={length}
            fieldValue={fieldValue}
            errorMessage={errorMessage}
            isError={isError}
            handleChange={handleChange}
            handleKeyDown={handleKeyDown}
            inputRefs={inputRefs}
            sx={sx}
            rest={rest}
          />
        );
      }}
    />
  );
};

export default InputOtp;
